import { apiFetch, getStoredUser, parseApiError } from './client';
import {
  INTEGRATIONS_MOCK,
  INTEGRATION_LOGOS,
  buildInstalledFromCatalog,
} from '../data/integrationsMock';
import { readJson, removeKey, writeJson } from '../lib/persist';

const STORAGE_PREFIX = 'vi.integrations.mock.v1';
const OAUTH_PROVIDERS = new Set(['dynamics']);
const API_KEY_PROVIDERS = new Set(['klaviyo']);

function storageKey() {
  const user = getStoredUser();
  const accountId = user?.accountId || user?.id || 'anon';
  return `${STORAGE_PREFIX}:${accountId}`;
}

function cloneMock() {
  return JSON.parse(JSON.stringify(INTEGRATIONS_MOCK));
}

function loadMockStore() {
  const stored = readJson(localStorage, storageKey(), null);
  if (!stored || typeof stored !== 'object' || !Array.isArray(stored.items)) {
    return cloneMock();
  }
  return {
    ...cloneMock(),
    ...stored,
    items: stored.items,
  };
}

function persistMockStore() {
  writeJson(localStorage, storageKey(), mockStore);
}

let mockStore = loadMockStore();
let boundStorageKey = storageKey();

export function resetIntegrationsMockStore() {
  mockStore = cloneMock();
  removeKey(localStorage, storageKey());
  boundStorageKey = storageKey();
}

function withCatalogMetadata(item) {
  if (!item) return item;
  const catalogEntry = buildInstalledFromCatalog(item.provider || item.id);
  if (!catalogEntry) return null;
  const logoUrl = item.logoUrl || catalogEntry.logoUrl || INTEGRATION_LOGOS[item.logoKey] || null;

  return {
    ...catalogEntry,
    ...item,
    logoUrl,
    available: catalogEntry.available,
    capabilities: catalogEntry.capabilities,
    docsHint: catalogEntry.docsHint,
    category: catalogEntry.category,
    name: catalogEntry.name,
    shortName: catalogEntry.shortName,
    description: catalogEntry.description,
    outcome: catalogEntry.outcome,
  };
}

function withLogos(data) {
  return {
    ...data,
    items: (data.items || []).map(withCatalogMetadata).filter(Boolean),
  };
}

function normalizeAccountId(accountIdOrOptions) {
  if (accountIdOrOptions && typeof accountIdOrOptions === 'object') {
    return accountIdOrOptions.accountId;
  }
  return accountIdOrOptions;
}

function withAccountId(path, accountIdOrOptions) {
  const accountId = normalizeAccountId(accountIdOrOptions);
  if (accountId == null) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}accountId=${encodeURIComponent(accountId)}`;
}

async function tryLiveJson(path, options, fallbackMessage) {
  const response = await apiFetch(path, options);
  if (response.status === 404) {
    const err = new Error('Integrations API not available yet');
    err.code = 'NOT_IMPLEMENTED';
    throw err;
  }
  if (!response.ok) {
    throw new Error(await parseApiError(response, fallbackMessage));
  }
  return response.json();
}

function bumpMockUpdatedAt() {
  mockStore.updatedAt = new Date().toISOString();
  persistMockStore();
}

function findMockItem(provider) {
  return mockStore.items.find((item) => item.provider === provider || item.id === provider);
}

function ensureStoreForCurrentAccount() {
  const key = storageKey();
  if (key === boundStorageKey) return;
  boundStorageKey = key;
  mockStore = loadMockStore();
}

export async function fetchIntegrations(options = {}) {
  const { allowMock = true, accountId } = options;
  try {
    const data = await tryLiveJson(
      withAccountId('/api/integrations', accountId),
      { method: 'GET' },
      'Failed to load integrations'
    );
    return { data: withLogos(data), source: 'api' };
  } catch (error) {
    if (allowMock === false) throw error;
    ensureStoreForCurrentAccount();
    return { data: withLogos(JSON.parse(JSON.stringify(mockStore))), source: 'mock' };
  }
}

export async function addIntegration(provider, accountIdOrOptions) {
  try {
    const data = await tryLiveJson(
      withAccountId(`/api/integrations/${provider}/install`, accountIdOrOptions),
      { method: 'POST' },
      `Failed to add ${provider}`
    );
    return { data: withCatalogMetadata(data), source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') throw error;
    ensureStoreForCurrentAccount();
    if (findMockItem(provider)) {
      throw new Error('Already added to your workspace.');
    }
    const installed = buildInstalledFromCatalog(provider);
    if (!installed) throw new Error('Unknown integration.');
    if (!installed.available) {
      throw new Error(`${installed.shortName} is coming soon and cannot be added yet.`);
    }
    mockStore.items.push(installed);
    bumpMockUpdatedAt();
    return { data: withCatalogMetadata(JSON.parse(JSON.stringify(installed))), source: 'mock' };
  }
}

export async function removeIntegration(provider, accountIdOrOptions) {
  try {
    const data = await tryLiveJson(
      withAccountId(`/api/integrations/${provider}/install`, accountIdOrOptions),
      { method: 'DELETE' },
      `Failed to remove ${provider}`
    );
    return { data, source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') throw error;
    ensureStoreForCurrentAccount();
    const index = mockStore.items.findIndex(
      (item) => item.provider === provider || item.id === provider
    );
    if (index < 0) throw new Error('Integration is not in your workspace.');
    mockStore.items.splice(index, 1);
    bumpMockUpdatedAt();
    return { data: { provider, removed: true }, source: 'mock' };
  }
}

export async function connectIntegration(provider, payload = {}, accountIdOrOptions) {
  if (OAUTH_PROVIDERS.has(provider)) {
    return startOAuthConnect(provider, accountIdOrOptions);
  }

  if (API_KEY_PROVIDERS.has(provider) && !payload.apiKey) {
    throw new Error(`${provider} requires an API key to connect.`);
  }

  try {
    const data = await tryLiveJson(
      withAccountId(`/api/integrations/${provider}/connect`, accountIdOrOptions),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      `Failed to connect ${provider}`
    );
    return { data: withCatalogMetadata(data), source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') throw error;
    ensureStoreForCurrentAccount();
    let item = findMockItem(provider);
    if (!item) {
      const installed = buildInstalledFromCatalog(provider);
      if (!installed?.available) {
        throw new Error(installed?.docsHint || `${provider} is not available yet`);
      }
      mockStore.items.push(installed);
      item = findMockItem(provider);
    }
    if (!item.available) {
      throw new Error(item.docsHint || `${provider} is not available yet`);
    }
    const now = new Date().toISOString();
    item.status = 'connected';
    item.connectedAt = now;
    item.lastSyncedAt = now;
    item.syncStatus = 'success';
    item.accountLabel = payload.accountLabel || `${item.shortName} sandbox`;
    item.errorMessage = null;
    bumpMockUpdatedAt();
    return { data: withCatalogMetadata(JSON.parse(JSON.stringify(item))), source: 'mock' };
  }
}

async function startOAuthConnect(provider, accountIdOrOptions) {
  const response = await apiFetch(
    withAccountId(`/api/integrations/${provider}/oauth/authorize`, accountIdOrOptions),
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(await parseApiError(response, `Failed to start ${provider} OAuth flow`));
  }
  const { authorizeUrl } = await response.json();
  window.location.href = authorizeUrl;
  return new Promise(() => {});
}

export async function disconnectIntegration(provider, accountIdOrOptions) {
  try {
    const data = await tryLiveJson(
      withAccountId(`/api/integrations/${provider}`, accountIdOrOptions),
      { method: 'DELETE' },
      `Failed to disconnect ${provider}`
    );
    return { data: withCatalogMetadata(data), source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') throw error;
    ensureStoreForCurrentAccount();
    const item = findMockItem(provider);
    if (!item) throw error;
    item.status = 'disconnected';
    item.connectedAt = null;
    item.lastSyncedAt = null;
    item.syncStatus = null;
    item.accountLabel = null;
    item.errorMessage = null;
    bumpMockUpdatedAt();
    return { data: withCatalogMetadata(JSON.parse(JSON.stringify(item))), source: 'mock' };
  }
}

export async function syncIntegration(provider, accountIdOrOptions) {
  try {
    const data = await tryLiveJson(
      withAccountId(`/api/integrations/${provider}/sync`, accountIdOrOptions),
      { method: 'POST' },
      `Failed to sync ${provider}`
    );
    return { data: withCatalogMetadata(data), source: 'api' };
  } catch (error) {
    if (error.code !== 'NOT_IMPLEMENTED') throw error;
    ensureStoreForCurrentAccount();
    const item = findMockItem(provider);
    if (!item || item.status !== 'connected') {
      throw new Error('Connect this integration before syncing.');
    }
    item.status = 'connected';
    item.lastSyncedAt = new Date().toISOString();
    item.syncStatus = 'success';
    item.errorMessage = null;
    bumpMockUpdatedAt();
    return { data: withCatalogMetadata(JSON.parse(JSON.stringify(item))), source: 'mock' };
  }
}
